import React, { useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Mirrors the backend validation in backend/src/routes/newsletter.ts
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status = 'idle' | 'submitting' | 'success' | 'already' | 'error';

interface NewsletterSignupProps {
  source: string;
  variant?: 'band' | 'footer';
  heading?: string;
  subtext?: string;
}

const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  source,
  variant = 'band',
  heading = 'One useful email a week.',
  subtext = 'The weekly tip, new Q&A answers, and webinar invites — free, no spam, unsubscribe anytime.',
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      setStatus('error');
      return;
    }

    setStatus('submitting');
    try {
      const response = await axios.post(`${API_BASE_URL}/newsletter/subscribe`, {
        email: normalized,
        source,
      });
      setStatus(response.data?.alreadySubscribed ? 'already' : 'success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  const subscribed = status === 'success' || status === 'already';

  const statusLine = subscribed ? (
    <p className="newsletter-status ok">
      {status === 'already'
        ? 'That email is already on the list — you’re all set.'
        : 'You’re in — the next issue lands in your inbox.'}
    </p>
  ) : status === 'error' ? (
    <p className="newsletter-status err">Please enter a valid email address and try again.</p>
  ) : null;

  const form = !subscribed && (
    <form className="newsletter-form" onSubmit={handleSubmit} noValidate>
      <input
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (status === 'error') setStatus('idle');
        }}
        placeholder="you@example.com"
        aria-label="Email address"
      />
      <button className="btn btn-primary" type="submit" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Subscribing…' : 'Subscribe'}
      </button>
    </form>
  );

  if (variant === 'footer') {
    return (
      <div className="newsletter-footer">
        <p className="fine" style={{ marginTop: 16 }}>
          {heading}
        </p>
        {form}
        {statusLine}
      </div>
    );
  }

  return (
    <section className="newsletter-band" id="subscribe">
      <div className="wrap">
        <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
          Free weekly email
        </span>
        <h2>{heading}</h2>
        <p>{subtext}</p>
        {form}
        {statusLine}
      </div>
    </section>
  );
};

export default NewsletterSignup;
